import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiCheck, FiEye, FiSearch } from 'react-icons/fi';
import { getPendingUsers, verifyUser } from '../services/api';

interface PendingUser {
  userId: number;
  userType: 'dealer' | 'contractor';
  fullName: string;
  mobile: string;
  email?: string;
  city?: string;
  state?: string;
  address?: string;
  pinCode?: string;
  isActive: boolean;
  isVerified: boolean;
  balance: number;
  totalEarned: number;
  totalRedeemed: number;
  scanCount: number;
  hasPreRegistration: boolean;
  preRegisteredUserId?: number | null;
  preRegLinkedUserId?: number | null;
  createdAt: string;
}

const pageSize = 20;

export default function PendingUsers() {
  const [users, setUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [userType, setUserType] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState<PendingUser | null>(null);

  const loadUsers = useCallback(async (searchValue = search) => {
    try {
      setLoading(true);
      const res = await getPendingUsers({
        page,
        pageSize,
        search: searchValue,
        userType,
      });
      setUsers(res.data.data ?? []);
      setTotalCount(res.data.totalCount ?? 0);
    } catch (error) {
      console.error('Failed to load pending users:', error);
      toast.error('Unable to load pending users');
    } finally {
      setLoading(false);
    }
  }, [page, search, userType]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = () => {
    setPage(1);
    loadUsers(search);
  };

  const handleApprove = async (user: PendingUser) => {
    try {
      await verifyUser(user.userId);
      toast.success('User approved and added to pre-registered users');
      if (selectedUser?.userId === user.userId) {
        setSelectedUser(null);
      }
      loadUsers();
    } catch (error: any) {
      console.error('Failed to approve user:', error);
      toast.error(error.response?.data?.message || 'Unable to approve user');
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Pending Approval</h1>
        <p>Approve dealer and contractor accounts that can scan and earn points, but still need redemption access.</p>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-filters">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, mobile, city or PIN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <select
              value={userType}
              onChange={(e) => {
                setUserType(e.target.value);
                setPage(1);
              }}
              className="filter-dropdown"
            >
              <option value="">All Types</option>
              <option value="dealer">Dealer</option>
              <option value="contractor">Contractor</option>
            </select>
            <button className="btn btn-outline" onClick={handleSearch}>
              <FiSearch /> Search
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Type</th>
                <th>Location</th>
                <th>Wallet</th>
                <th>Activity</th>
                <th>Pre-Registered</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center">Loading pending users...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center">No pending users found</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.userId}>
                    <td>
                      <div className="stack-sm">
                        <strong>{user.fullName}</strong>
                        <span className="text-muted">{user.mobile}</span>
                        {user.email && <span className="text-muted">{user.email}</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${user.userType === 'dealer' ? 'badge-success' : 'badge-info'}`}>
                        {user.userType}
                      </span>
                    </td>
                    <td>
                      <div className="stack-sm">
                        <span>{user.city || '-'}</span>
                        <span className="text-muted">{user.state || user.pinCode || 'Location missing'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="stack-sm">
                        <span>{user.balance.toLocaleString('en-IN')} pts</span>
                        <span className="text-muted">Earned {user.totalEarned.toLocaleString('en-IN')}</span>
                      </div>
                    </td>
                    <td>
                      <div className="stack-sm">
                        <span>{user.scanCount} scans</span>
                        <span className="text-muted">Redeemed {user.totalRedeemed.toLocaleString('en-IN')}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${user.hasPreRegistration ? 'badge-info' : 'badge-warning'}`}>
                        {user.hasPreRegistration ? 'Exists' : 'Will be added'}
                      </span>
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => setSelectedUser(user)}
                          title="View Details"
                        >
                          <FiEye />
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleApprove(user)}
                          title="Approve User"
                        >
                          <FiCheck /> Approve
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <span className="page-info">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approval Details</h3>
              <button className="modal-close" onClick={() => setSelectedUser(null)}>×</button>
            </div>
            <div className="details-grid">
              <div className="detail-item">
                <label>Name</label>
                <span>{selectedUser.fullName}</span>
              </div>
              <div className="detail-item">
                <label>Mobile</label>
                <span>{selectedUser.mobile}</span>
              </div>
              <div className="detail-item">
                <label>User Type</label>
                <span>{selectedUser.userType}</span>
              </div>
              <div className="detail-item">
                <label>City</label>
                <span>{selectedUser.city || '-'}</span>
              </div>
              <div className="detail-item">
                <label>State</label>
                <span>{selectedUser.state || '-'}</span>
              </div>
              <div className="detail-item">
                <label>PIN Code</label>
                <span>{selectedUser.pinCode || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Address</label>
                <span>{selectedUser.address || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Wallet Balance</label>
                <span>{selectedUser.balance.toLocaleString('en-IN')} pts</span>
              </div>
              <div className="detail-item">
                <label>Pre-Registration</label>
                <span>
                  {selectedUser.hasPreRegistration
                    ? 'Existing record will be linked and refreshed'
                    : 'A new pre-registered user will be created on approval'}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSelectedUser(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => handleApprove(selectedUser)}>
                <FiCheck /> Approve & Enable Redemption
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
